import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { MotifButton, MotifProgressLoader } from "@ey-xd/motif-react";
import { getImagePath } from "../../common/utility/utility";
import { fetchSourceDocumentUrl } from "./chatbotService";

function getFileType(fileName) {
  return fileName?.split(".").pop()?.toLowerCase() ?? "";
}

// Browsers have no native renderer for Office formats, so pointing an iframe
// directly at the blob SAS URL triggers a download instead of a preview.
// Route all Office types through the Office Online viewer embed; PDF is the
// only type browsers can render natively in an iframe.
const OFFICE_VIEWER_TYPES = ["doc", "docx", "ppt", "pptx", "xls", "xlsx", "xlsm", "xlsb"];

// Office Online Viewer is fetched server-side by Microsoft's own servers, not
// the user's browser, so it can't reach a blob URL that's network-restricted
// to the corporate network. The partners flow works around this by proxying
// through its own public-facing backend (api/partners/viewfile) before handing
// the URL to the viewer. The ai-chatbot backend doesn't have an equivalent
// endpoint yet, so this temporarily reuses the partners' proxy; swap it for a
// dedicated chatbot endpoint once the BE team adds one.
const VIEW_FILE_PROXY_BASE_URL = process.env.REACT_APP_API_URL_US;

function officeViewerEmbedUrl(fileUrl) {
  const proxiedUrl = `${VIEW_FILE_PROXY_BASE_URL}/api/partners/viewfile?sasUrl=${encodeURIComponent(fileUrl)}`;
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(proxiedUrl)}`;
}

function previewUrl(fileUrl, fileType) {
  if (OFFICE_VIEWER_TYPES.includes(fileType)) {
    return officeViewerEmbedUrl(fileUrl);
  }
  return fileUrl;
}

export function SourceDocViewer({ fileName, onClose }) {
  const [fileUrl, setFileUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fileType = getFileType(fileName);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setFileUrl("");
    fetchSourceDocumentUrl(fileName)
      .then((url) => {
        if (active) setFileUrl(url);
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [fileName]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="source-document-title">
      <DialogTitle id="source-document-title" style={{ paddingRight: 64, textAlign: "left" }}>
        <div style={{ alignItems: "center", display: "flex", gap: 12, minWidth: 0 }}>
          <img src={getImagePath(fileName)} alt="" style={{ height: 28, width: 28 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={fileName}>
            {fileName}
          </span>
        </div>
        <IconButton
          aria-label="Close document viewer"
          onClick={onClose}
          style={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers style={{ height: 500, padding: 0 }}>
        {loading && <MotifProgressLoader show variant="default" />}
        {!loading && error && (
          <div style={{ alignItems: "center", display: "flex", height: "100%", justifyContent: "center", padding: 16 }}>
            Unable to preview this document.
          </div>
        )}
        {!loading && !error && fileUrl && (
          <iframe
            src={previewUrl(fileUrl, fileType)}
            title="Source document"
            style={{ border: "none", height: "100%", width: "100%" }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <MotifButton onClick={onClose} variant="primary">Close</MotifButton>
      </DialogActions>
    </Dialog>
  );
}

export default SourceDocViewer;
